import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, TrendingUp, Lightbulb, Target } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface ATSRecommendationsProps {
  criticalIssues: Array<{ issue: string; location: string; fix: string }>;
  opportunities: string[];
  industryInsights?: {
    industry: string;
    recommendations: string[];
  };
}

export const ATSRecommendations = ({ 
  criticalIssues, 
  opportunities,
  industryInsights 
}: ATSRecommendationsProps) => {
  return (
    <div className="space-y-4">
      {/* Priority Recommendations */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <AlertCircle className="h-6 w-6 text-destructive" />
          High Priority - Do First
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          These fixes will have the biggest impact on your ATS score.
        </p>
        {criticalIssues.length > 0 ? (
          <div className="space-y-3">
            {criticalIssues.slice(0, 5).map((issue, index) => (
              <div key={index} className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Badge variant="destructive" className="mt-1">
                    Priority {index + 1}
                  </Badge>
                  <div className="flex-1">
                    <p className="font-medium text-foreground mb-2">{issue.issue}</p>
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Fix:</strong> {issue.fix}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-green-600">✓ No critical issues found!</p>
        )}
      </Card>

      {/* Medium Priority */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="h-6 w-6 text-yellow-600" />
          Medium Priority
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Improvements that will boost your score and competitiveness.
        </p>
        {opportunities.length > 0 ? (
          <div className="space-y-2">
            {opportunities.map((opportunity, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-muted/50 border border-border rounded-lg">
                <TrendingUp className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-foreground">{opportunity}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No medium priority items at this time.</p>
        )}
      </Card>

      {/* Industry-Specific Recommendations */}
      {industryInsights && (
        <Card className="p-6 border-primary/50">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Target className="h-6 w-6 text-primary" />
            {industryInsights.industry} Industry Best Practices
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Recommendations specific to your industry to maximize ATS performance.
          </p>
          <div className="space-y-2">
            {industryInsights.recommendations.map((rec, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm text-foreground">{rec}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* General Tips */}
      <Card className="p-6 bg-muted/50">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Lightbulb className="h-6 w-6" />
          General ATS Tips
        </h3>
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="formatting">
            <AccordionTrigger>Formatting Best Practices</AccordionTrigger>
            <AccordionContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Use standard fonts (Arial, Calibri, Times New Roman)</li>
                <li>• Keep font size between 10-12pt</li>
                <li>• Use standard section headers (Work Experience, Education, Skills)</li>
                <li>• Avoid headers, footers, text boxes, and tables for layout</li>
                <li>• Save as .docx format for best compatibility</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="keywords">
            <AccordionTrigger>Keyword Optimization</AccordionTrigger>
            <AccordionContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Mirror language from the job description</li>
                <li>• Include both acronyms and full terms (SEO and Search Engine Optimization)</li>
                <li>• Use keywords in context, not just in a list</li>
                <li>• Target 2-4% keyword density</li>
                <li>• Include industry-specific terminology</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="content">
            <AccordionTrigger>Content Guidelines</AccordionTrigger>
            <AccordionContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Start bullet points with strong action verbs</li>
                <li>• Include quantifiable achievements (numbers, percentages, $)</li>
                <li>• Keep descriptions clear and concise</li>
                <li>• Use consistent date formatting (MM/YYYY)</li>
                <li>• Spell check everything - ATS flags errors</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>
    </div>
  );
};
